import { useQueryClient } from '@tanstack/react-query';
import { ApiError, useApi } from '@/hooks/useApi';
import { useModeAwareMutation } from '@/hooks/useModeAwareQuery';
import { invalidateCustomerListQueries } from '@/lib/query/customer-list-queries';

export interface BulkUpdateCustomersRequest {
  customerIds: number[];
  collectionStrategyId?: number;
  creditControllerId?: number;
  accountManagerId?: number;
  riskLevelId?: number | null;
  clearRiskLevel?: boolean;
  clearCollectionStrategy?: boolean;
  clearCreditController?: boolean;
  clearAccountManager?: boolean;
}

interface BulkUpdateCustomersResponse {
  updated: number;
  message: string;
}

export function useBulkUpdateCustomers() {
  const { apiBaseUrl, getHeaders } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<BulkUpdateCustomersResponse, Error, BulkUpdateCustomersRequest>(
    async (request) => {
      const headers = await getHeaders();
      const response = await fetch(`${apiBaseUrl}/api/v1/customers/bulk-update`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
      }

      return response.json();
    },
    (request) => {
      return {
        updated: request.customerIds.length,
        message: `${request.customerIds.length} customer(s) updated successfully`,
      };
    },
    {
      onSuccess: async () => {
        await invalidateCustomerListQueries(queryClient);
        await queryClient.invalidateQueries({ queryKey: ['customer-audit-logs'] });
      },
    },
  );
}
